import {decode} from 'he';
import trim from 'lodash/trim';
import {getEnv} from '../../../../../../common/env/env';
import {getKageUrl} from '../../../../../../common/shared/utils/getKageUrl';
import {getInternalAssetImage} from '../../../../../../design-system/atoms/StaticImage/getInternalAssetImage.function';
import {InternalStaticImageType} from '../../../../../../design-system/atoms/StaticImage/InternalStaticImageType.enum';
import {STATIC_IMAGE_INFO_MAP} from '../../../../../../design-system/atoms/StaticImage/StaticImageInfoMap.constant';
import {ContentParagraph} from '../type/ContentParagraph.interface';
import {cloneAttributes} from './cloneAttributes';
import {createEmptyImageDataUri} from './createEmptyImageDataUri';

/**
 * 텍스트뷰어 코어 API로 받은 문단 정보를 텍스트뷰어 문단으로 변경하는 함수
 */
export function getParagraphNode(
  {
    id,
    type,
    text,
    attributes: _attributes,
    image: _image,
    childParagraphList: _childParagraphList,
  }: ContentParagraph,
  svgData?: {xmlNs?: string; xlink?: string; version?: string}
): Node {
  // text 타입일 때는 순수한 TEXT 노드라 태그에 래핑되어있지 않아 스타일이나 자식노드가 없습니다.
  if (type === 'TEXT') {
    const textNode = document.createTextNode(decode(text));
    return textNode;
  }

  let element: Element = document.createElement(type);
  let _svgData = svgData;

  const {class: _class, style: _style, ...restAttributes} = _attributes || {};

  if (_class) {
    const classNameList = _class
      .split(/\s+/)
      .map(trim)
      .filter(name => !!name);
    element.classList.add(...classNameList);
  }

  if (_style) {
    element.setAttribute('style', _style);
  }

  if (restAttributes) {
    Object.entries(restAttributes)
      .filter(([key]) => !key.includes(':')) // 키에 ':' 이 들어가 있다면 setAttributeNS 와 같이 네임스페이스가 포함되는 XML 속성이므로 여기서 제외합니다.
      .forEach(([key, value]) => {
        // 터치나 클릭 이벤트가 요소에 직접 붙어 있었던 경우는 제거합니다.
        if (key.startsWith('ontouch') || key.startsWith('onclick')) {
          return;
        }
        element.setAttribute(key, value);
      });
  }

  // 각 요소에 문단 ID를 추가합니다.
  // XXX: 모든 요소에 붇이는게 과할 경우, 외부에서 최상위 문단에서만 붙이고 여기서는 제거하는 것도 괜찮은 듯합니다.
  element.setAttribute('data-p-id', id);

  if (_svgData) {
    const newElement = document.createElementNS(
      _svgData.xmlNs || _attributes?.['xmlns'] || SVG_XML_NS,
      type
    );
    cloneAttributes(newElement, element);
    element = newElement;

    // SVG 이미지 요소는 기본적으로 width / height 를 가지는게 스펙입니다. 없으면 0 으로 취급합니다.
    // 로드 상태와 상관없이 사이즈가 결정될 가능성이 높으니 원본을 바로 사용합니다.
    if (type === 'IMAGE') {
      const xlinkNs =
        _svgData.xlink || _attributes?.['xmlns:xlink'] || SVG_XLINK_NS;
      if (_image) {
        const {imageSrcKey, imageWidth, imageHeight, imageFilename, isCover} =
          _image;
        const fullUrl = getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: imageSrcKey,
        });

        element.setAttributeNS(xlinkNs, 'href', fullUrl);

        element.setAttribute('data-width', String(imageWidth));
        element.setAttribute('data-height', String(imageHeight));
        element.setAttribute('data-ratio', String(imageHeight / imageWidth));
        element.setAttribute('data-name', String(imageFilename));
        element.setAttribute('data-is-cover', String(isCover));
      } else {
        // 어색하게 보이지 않게, 임시 방편으로 로딩 이미지가 나오게 합니다.
        // 따라서 페이지 계산할 때 이슈가 될 수 있습니다.
        const loadingImageErrorInfo =
          STATIC_IMAGE_INFO_MAP[
            InternalStaticImageType.LoadingImageErrorWebStatic
          ];
        element.setAttributeNS(
          xlinkNs,
          'href',
          getInternalAssetImage(loadingImageErrorInfo.srcStatic || '')
        );
        element.setAttribute('data-is-loading-image-error', 'true');
      }
    }
  } else if (type === 'IMG') {
    if (_image) {
      const {imageSrcKey, imageWidth, imageHeight, imageFilename, isCover} =
        _image;
      const fullUrl = getKageUrl({
        kageUrlPrefix: getEnv().kageUrlPrefix,
        kageId: imageSrcKey,
      });
      const imageDataUri = createEmptyImageDataUri({
        width: imageWidth,
        height: imageHeight,
      });
      // 최초에 보여지는 소스는 빈 데이터입니다.
      element.setAttribute('src', EMPTY_DATA_URI);
      element.setAttribute('data-width', String(imageWidth));
      element.setAttribute('data-height', String(imageHeight));
      element.setAttribute('data-ratio', String(imageHeight / imageWidth));
      element.setAttribute('data-name', String(imageFilename));
      element.setAttribute('data-is-cover', String(isCover));
      // 이미지를 그릴 때 위치를 빠르게 잡기 위해 사용할 더미 이미지 소스입니다.
      element.setAttribute('data-dummy-src', imageDataUri);
      // 원본 이미지 소스입니다.
      element.setAttribute('data-src', fullUrl);
      const originalAlt = element.getAttribute('alt');
      if (!originalAlt) {
        element.setAttribute('alt', '');
      }
    }
  } else if (type === 'A') {
    // 앵커 요소는 '#' 같이 불필요하게 오동작하거나 뷰어를 이탈시킬 수 있는 링크가 생기지 않도록 href가 있으면 제거합니다.
    element.removeAttribute('href');
  } else if (type === 'OBJECT') {
    // 오브젝트 요소의 데이터로는 이미지, 비디오, 오디오 등이 될 수 있는데,
    // 현재 텍스트뷰어 엔진의 처리 과정에서 이미지 이외의 리소스는 CDN에 업로드 하는 처리를 하지 않고 있습니다.
    // 일단 원본 정보는 살려둡니다.
    const originalType = element.getAttribute('type');
    if (originalType) {
      element.setAttribute('data-original-type', originalType);
      element.setAttribute('type', '');
    }
    const originalData = element.getAttribute('data');
    if (originalData) {
      element.setAttribute('data-original-data', originalData);
      element.setAttribute('data', '');
    }

    // 이미지일 경우 이미지 요소와 차이가 없으므로 이미지 요소로 대체합니다.
    // 단, 이 때에는 object 요소에 자체에 걸린 스타일을 적용할 수 없습니다.
    if (_image) {
      const {imageSrcKey, imageWidth, imageHeight, imageFilename, isCover} =
        _image;
      const fullUrl = getKageUrl({
        kageUrlPrefix: getEnv().kageUrlPrefix,
        kageId: imageSrcKey,
      });
      const imageDataUri = createEmptyImageDataUri({
        width: imageWidth,
        height: imageHeight,
      });

      const newElement = document.createElement('img');
      cloneAttributes(newElement, element);
      element = newElement;

      // 최초에 보여지는 소스는 빈 데이터입니다.
      element.setAttribute('src', EMPTY_DATA_URI);
      element.setAttribute('data-width', String(imageWidth));
      element.setAttribute('data-height', String(imageHeight));
      element.setAttribute('data-ratio', String(imageHeight / imageWidth));
      element.setAttribute('data-name', String(imageFilename));
      element.setAttribute('data-is-cover', String(isCover));
      // 이미지를 그릴 때 위치를 빠르게 잡기 위해 사용할 더미 이미지 소스입니다.
      element.setAttribute('data-dummy-src', imageDataUri);
      // 원본 이미지 소스입니다.
      element.setAttribute('data-src', fullUrl);
      const originalAlt = element.getAttribute('alt');
      if (!originalAlt) {
        element.setAttribute('alt', '');
      }
    } else {
      // 어색하게 보이지 않게, 임시 방편으로 로딩 이미지가 나오게 합니다.
      // 오브젝트 요소는 렌더링 이후 이미지 사이즈 고정시킬 때 대상이 아니라서 사이즈가 고정되지 않습니다.
      // 따라서 페이지 계산할 때 이슈가 될 수 있습니다.
      const loadingImageErrorInfo =
        STATIC_IMAGE_INFO_MAP[
          InternalStaticImageType.LoadingImageErrorWebStatic
        ];
      element.setAttribute('type', '');
      element.setAttribute(
        'data',
        getInternalAssetImage(loadingImageErrorInfo.srcStatic || '')
      );
      element.setAttribute('data-is-loading-image-error', 'true');
    }
  } else if (type === 'SVG') {
    const xmlNs = _attributes?.['xmlns'];
    const xlink = _attributes?.['xmlns:xlink'];
    const version = _attributes?.['version'];

    const newElement = document.createElementNS(xmlNs || SVG_XML_NS, 'svg');
    cloneAttributes(newElement, element);
    element = newElement;

    // 하위 노드에서 사용할 수 있게 svgData를 넘겨줍니다.
    _svgData = {xmlNs, xlink, version};
  } else if (text) {
    const textNode = document.createTextNode(decode(text));
    element.appendChild(textNode);
  }

  if (_childParagraphList) {
    for (let index = 0; index < _childParagraphList.length; index++) {
      const paragraph = _childParagraphList[index];
      const childNode = getParagraphNode(paragraph, _svgData);
      element.appendChild(childNode);
    }
  }

  return element;
}

// 이 데이터 URI는 유효하며 불필요한 네트워크를 발생시키지 않습니다.
// https://stackoverflow.com/questions/5775469/whats-the-valid-way-to-include-an-image-with-no-src/53365710#53365710 참고
const EMPTY_DATA_URI = 'data:,';

const SVG_XML_NS = 'http://www.w3.org/2000/svg';
const SVG_XLINK_NS = 'http://www.w3.org/1999/xlink';
